import { FormEvent, useState } from "react";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { ErrorBoxComponent } from "components/Error/ErrorBoxComponent";
import { TenantHomeDashboardNavigationBar } from "components/LayoutComponent/VerticalLayout/HeaderBar";
import { getQuickAccessNavigateURL } from "components/LayoutComponent/VerticalLayout/QuickAccess";
import { useDashboardNaviagationPrefetchHandler } from "hooks/useDashboardNavigationPrefetchHandler";
import { MyViewHeader } from "pages/Tenant/CustomerDashboardPage/MyViewHeader";
import { useGetMyPersonalization } from "services/v1/Tenant/ConfigurationService";
import { useSearchModelCatalogDashboards } from "services/v1/Tenant/ModelCatalogService";
import { RootState } from "store";
import { PAGE_REF_NAVIGATION } from "utils/Animation";

const DEFAULT_QUERY = "";
const SUGGESTED_SEARCHES = [
  "Where can I compare average driver wages per kilometre across trucks?",
  "Show me revenue per trip and revenue per kilometre for our vehicle operations.",
  "Which dashboard shows truck profitability, including fuel and maintenance costs per kilometre?",
];
const MAX_VISIBLE_ATTRIBUTES = 3;

function getMatchedAttributeLabel(count: number) {
  return `${count} matching ${count === 1 ? "attribute" : "attributes"}`;
}

function ResultsLoadingSkeleton() {
  return (
    <Box
      aria-label="Loading catalog results"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(3, minmax(0, 1fr))",
        },
        gap: 2,
      }}
    >
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton
          key={index}
          variant="rounded"
          height={168}
          animation="wave"
          sx={{ borderRadius: 2 }}
        />
      ))}
    </Box>
  );
}

export default function TenantHomeSearchPage() {
  const navigate = useNavigate();
  const role = useSelector((state: RootState) => state.auth.role);
  const prefetchHandler = useDashboardNaviagationPrefetchHandler();
  const { data: personalizationData, isLoading: isPersonalizationLoading } =
    useGetMyPersonalization({ enabled: true });
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [searchedQuery, setSearchedQuery] = useState(DEFAULT_QUERY);

  const normalizedSearchedQuery = searchedQuery.trim();
  const { data, error, isError, isFetching, isLoading } =
    useSearchModelCatalogDashboards(
      normalizedSearchedQuery,
      Boolean(normalizedSearchedQuery)
    );
  const result = data?.data;
  const records = result?.records ?? [];

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return;

    setQuery(normalizedQuery);
    setSearchedQuery(normalizedQuery);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setSearchedQuery(suggestion);
  };

  const handleClear = () => {
    setQuery("");
    setSearchedQuery("");
  };

  const isPending =
    Boolean(normalizedSearchedQuery) && (isLoading || isFetching);

  const handleDashboardResultClick = (dashboardId: string) => {
    const dashboard = personalizationData?.data.dashboards.find(
      (item) => String(item.id) === String(dashboardId)
    );
    const firstPage = dashboard?.myViews[0];
    if (!firstPage) return;

    prefetchHandler(firstPage, dashboard.myViews[1]);
    const navigateURL = getQuickAccessNavigateURL(
      firstPage,
      true,
      PAGE_REF_NAVIGATION.LEFT,
      role
    );
    if (navigateURL) navigate(navigateURL);
  };

  return (
    <TenantHomeDashboardNavigationBar
      breadCrumbs={[]}
      containerSx={{
        minHeight: "calc(100vh - 112px)",
        display: "flex",
        flexDirection: "column",
      }}
      rootContainerSx={{ minHeight: "100vh", height: "auto" }}
    >
      <Box
        sx={{
          display: "flex",
          minHeight: "100%",
          flex: 1,
          flexDirection: "column",
        }}
      >
        <Box px={2} pt={2}>
          <MyViewHeader />
        </Box>

        <Stack
          spacing={{ xs: 2.5, md: 3 }}
          px={{ xs: 2, sm: 3, lg: 5 }}
          py={{ xs: 3, md: 4 }}
          flex={1}
        >
          <Box>
            <Typography
              component="h1"
              sx={{ color: "#2D3448", fontSize: 21, fontWeight: 800 }}
            >
              AI Query
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>
              Ask a question to quickly find the dashboard and information you
              need to plan and manage your business.
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSearch}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <OutlinedInput
                autoFocus
                fullWidth
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Ask about revenue, costs, performance, or daily operations"
                inputProps={{ "aria-label": "Search dashboards" }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: "#3F50B5" }} />
                  </InputAdornment>
                }
                endAdornment={
                  query ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Clear search"
                        edge="end"
                        size="small"
                        onClick={handleClear}
                      >
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined
                }
                sx={{
                  height: 54,
                  borderRadius: 2,
                  bgcolor: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#AAB4E7",
                    borderWidth: 1.5,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3F50B5",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3F50B5",
                    borderWidth: 2,
                  },
                }}
              />
              <Button
                type="submit"
                variant="main-table-panel"
                disabled={!query.trim() || isPending}
                startIcon={<SearchRoundedIcon />}
                sx={{
                  minWidth: { sm: 150 },
                  height: 54,
                  borderRadius: 2,
                  fontWeight: 800,
                  boxShadow: "none",
                }}
              >
                Research
              </Button>
            </Stack>
          </Box>

          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography
              variant="body2"
              fontWeight={800}
              color="#41495C"
              mr={0.25}
            >
              Suggested queries
            </Typography>
            {SUGGESTED_SEARCHES.map((suggestion) => {
              const isActive =
                searchedQuery.toLowerCase() === suggestion.toLowerCase();
              return (
                <Button
                  key={suggestion}
                  size="small"
                  variant="outlined"
                  aria-pressed={isActive}
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{
                    minHeight: 32,
                    borderRadius: 999,
                    px: 2,
                    textTransform: "none",
                    borderColor: isActive ? "#3F50B5" : "#CFD6E4",
                    bgcolor: isActive ? "#3F50B5" : "#fff",
                    color: isActive ? "#fff" : "#566074",
                    fontSize: 12,
                    fontWeight: 700,
                    "&:hover": {
                      borderColor: "#3F50B5",
                      bgcolor: isActive ? "#334294" : "#F4F6FC",
                    },
                  }}
                >
                  {suggestion}
                </Button>
              );
            })}
          </Stack>

          {normalizedSearchedQuery ? (
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <Typography
                  component="h2"
                  sx={{
                    color: "#30384B",
                    fontSize: { xs: 16, md: 18 },
                    fontWeight: 800,
                  }}
                >
                  Results for “{searchedQuery}”
                </Typography>
                {!isPending && !isError && result && (
                  <Typography
                    variant="caption"
                    color="primary.main"
                    fontWeight={800}
                    whiteSpace="nowrap"
                  >
                    {result.total} {result.total === 1 ? "result" : "results"}
                  </Typography>
                )}
              </Stack>

              {isPending ? (
                <ResultsLoadingSkeleton />
              ) : isError ? (
                <ErrorBoxComponent error={error} />
              ) : records.length ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  {records.map((record) => {
                    const myViewDashboard =
                      personalizationData?.data.dashboards.find(
                        (item) => String(item.id) === String(record.dashboardId)
                      );
                    const firstPage = myViewDashboard?.myViews[0];
                    const visibleAttributes = record.matchedAttributes.slice(
                      0,
                      MAX_VISIBLE_ATTRIBUTES
                    );
                    const remainingAttributeCount = Math.max(
                      record.matchedAttributeCount - visibleAttributes.length,
                      0
                    );
                    return (
                      <ButtonBase
                        key={record.dashboardId}
                        aria-label={`Open first page of ${record.dashboardName}`}
                        disabled={!firstPage || isPersonalizationLoading}
                        onClick={() =>
                          handleDashboardResultClick(record.dashboardId)
                        }
                        sx={{
                          width: "100%",
                          minHeight: 220,
                          textAlign: "left",
                          alignItems: "stretch",
                          border: "1px solid #E4E7EC",
                          borderRadius: 2,
                          bgcolor: "#FFFFFF",
                          transition:
                            "background-color 180ms ease, border-color 180ms ease, transform 180ms ease",
                          "&:hover": {
                            borderColor: "#42BB93",
                            bgcolor: "#FCFFFE",
                            transform: "translateY(-2px)",
                          },
                          "&:hover .result-card-icon": {
                            bgcolor: "#E5F7EE",
                            color: "#2E8B62",
                            transform: "scale(1.06)",
                          },
                          "&:hover .result-card-arrow": {
                            color: "#42BB93",
                            transform: "translateX(3px)",
                          },
                          "&:focus-visible": {
                            outline: "3px solid rgba(63, 80, 181, 0.28)",
                            outlineOffset: 2,
                          },
                          "&.Mui-disabled": {
                            color: "inherit",
                            opacity: isPersonalizationLoading ? 0.7 : 1,
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          spacing={1.5}
                          width="100%"
                          p={2}
                        >
                          <Box
                            className="result-card-icon"
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              bgcolor: "#E9ECFA",
                              color: "#3F50B5",
                              flexShrink: 0,
                              transition:
                                "background-color 180ms ease, color 180ms ease, transform 180ms ease",
                            }}
                          >
                            <DashboardOutlinedIcon sx={{ fontSize: 21 }} />
                          </Box>
                          <Box minWidth={0} flex={1}>
                            <Typography
                              sx={{
                                color: "#30384B",
                                fontSize: 17,
                                fontWeight: 800,
                                lineHeight: 1.25,
                              }}
                            >
                              {record.dashboardName}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="#4D5870"
                              fontWeight={700}
                              mt={0.25}
                            >
                              {getMatchedAttributeLabel(
                                record.matchedAttributeCount
                              )}
                            </Typography>
                            <Stack spacing={1} mt={1.25}>
                              {visibleAttributes.map((attribute) => {
                                const componentNames = Array.from(
                                  new Set(
                                    attribute.matchedMyViewItems
                                      .map((item) => item.componentName)
                                      .filter(Boolean)
                                  )
                                );
                                return (
                                  <Box key={attribute.attributeId}>
                                    <Typography
                                      variant="body2"
                                      color="#30384B"
                                      fontWeight={800}
                                    >
                                      {attribute.displayName}
                                    </Typography>
                                    {attribute.description && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        {attribute.description}
                                      </Typography>
                                    )}
                                    {componentNames.length > 0 && (
                                      <Typography
                                        variant="caption"
                                        color="#667085"
                                        display="block"
                                        mt={0.25}
                                      >
                                        Found in {componentNames.join(", ")}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })}
                              {remainingAttributeCount > 0 && (
                                <Typography
                                  variant="caption"
                                  color="#3F50B5"
                                  fontWeight={800}
                                >
                                  +{remainingAttributeCount} more{" "}
                                  {remainingAttributeCount === 1
                                    ? "attribute"
                                    : "attributes"}
                                </Typography>
                              )}
                              {!isPersonalizationLoading && !firstPage && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={700}
                                >
                                  No My View pages configured
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                          {firstPage && (
                            <ChevronRightRoundedIcon
                              className="result-card-arrow"
                              sx={{
                                color: "#8E9AD2",
                                flexShrink: 0,
                                mt: 1,
                                transition:
                                  "color 180ms ease, transform 180ms ease",
                              }}
                            />
                          )}
                        </Stack>
                      </ButtonBase>
                    );
                  })}
                </Box>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  minHeight={230}
                  border="1px dashed #C9D1E3"
                  borderRadius={2}
                  bgcolor="#FAFBFD"
                  px={2}
                >
                  <SearchRoundedIcon
                    sx={{ color: "#98A2B3", fontSize: 42, mb: 1 }}
                  />
                  <Typography fontWeight={800} color="#30384B">
                    No dashboard matches found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    Try another attribute name, dashboard topic, or broader
                    business term.
                  </Typography>
                </Stack>
              )}
            </Stack>
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              minHeight={260}
              border="1px dashed #C9D1E3"
              borderRadius={2}
              bgcolor="#FAFBFD"
              px={2}
            >
              <SearchRoundedIcon
                sx={{ color: "#98A2B3", fontSize: 42, mb: 1 }}
              />
              <Typography fontWeight={800} color="#30384B">
                Find the right dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Ask about your operations, costs, revenue, or performance to see
                the most relevant dashboards.
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>
    </TenantHomeDashboardNavigationBar>
  );
}
